import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useApi, errMsg } from "@/lib/useApi";
import { Loader2, X } from "lucide-react";

export const BULK = {
  submit: { label: "Submit for review", from: ["draft"], roles: ["admin", "reviewer"], cls: "bg-blue-600 hover:bg-blue-700 text-white" },
  approve: { label: "Approve", from: ["in_review"], roles: ["admin", "reviewer"], cls: "bg-blue-600 hover:bg-blue-700 text-white" },
  publish: { label: "Publish", from: ["approved", "unpublished"], roles: ["admin"], cls: "bg-emerald-600 hover:bg-emerald-700 text-white" },
  direct_publish: { label: "Direct Publish (Admin Only)", from: ["draft", "in_review"], roles: ["admin"], cls: "bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-50",
    warn: "This bypasses the normal review and approval workflow. Each question is still validated (format, answer keys, mapping, syllabus, duplicates) and the audit log records “Direct publish by admin — review workflow bypassed”." },
  delete: { label: "Delete", from: ["draft", "in_review", "approved", "published", "unpublished", "archived"], roles: ["admin"], cls: "bg-white border border-red-300 text-red-700 hover:bg-red-50",
    warn: "Questions never used in any exam are permanently removed. Questions used in exam history (or currently published) are soft-deleted: hidden from the bank and exams but kept so past results stay intact, and restorable. This cannot be undone for permanently removed questions." },
};
const OUT_CLS = { success: "text-emerald-700", eligible: "text-emerald-700", skipped: "text-amber-700", failed: "text-red-700" };

export default function BulkActions({ selected, role, onClear, onDone, onOpen }) {
  const api = useApi();
  const [preview, setPreview] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState("");

  const run = async (action, ids, dry) => {
    setBusy(action);
    try { return (await api.post("/admin/questions/bulk-transition", { action, ids, dry_run: dry })).data; }
    catch (e) { toast.error(errMsg(e)); return null; }
    finally { setBusy(""); }
  };
  const check = async (action, ids) => { const r = await run(action, ids, true); if (r) { setReport(null); setPreview({ ...r, ids }); } };
  const confirm = async () => {
    const ids = preview.results.filter((x) => x.outcome === "eligible").map((x) => x.id);
    const r = await run(preview.action, ids, false);
    if (!r) return;
    const pre = preview.results.filter((x) => x.outcome !== "eligible");
    const results = [...r.results, ...pre];
    const summary = { success: r.summary.success, skipped: r.summary.skipped + pre.filter((x) => x.outcome === "skipped").length, failed: r.summary.failed + pre.filter((x) => x.outcome === "failed").length };
    setPreview(null); setReport({ action: r.action, results, summary });
    onDone(results.filter((x) => x.outcome === "success").map((x) => x.id));
  };

  if (!selected.length && !report) return null;
  const eligibleCount = (a) => selected.filter((q) => BULK[a].from.includes(q.status)).length;
  return (
    <>
      {selected.length > 0 && (
        <div className="sticky bottom-3 z-20 flex flex-wrap items-center gap-2 rounded-xl border border-blue-200 bg-white/95 backdrop-blur p-3 shadow-lg" data-testid="qb-bulk-bar">
          <span className="text-sm font-medium text-slate-900 mr-1" data-testid="qb-selected-count">{selected.length} selected</span>
          <span className="text-xs text-slate-500 mr-1">(eligible by status; full checks run before confirming)</span>
          {Object.entries(BULK).filter(([, v]) => v.roles.includes(role)).map(([a, v]) => (
            <Button key={a} size="sm" className={v.cls} disabled={!!busy || !eligibleCount(a)} onClick={() => check(a, selected.map((q) => q.id))} data-testid={`qb-bulk-${a}`}>
              {busy === a && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{v.label} <span className="ml-1 opacity-80" data-testid={`qb-bulk-${a}-eligible`}>({eligibleCount(a)})</span>
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={onClear} className="ml-auto" data-testid="qb-bulk-clear"><X className="h-4 w-4 mr-1" />Deselect all</Button>
        </div>
      )}
      <Dialog open={!!preview} onOpenChange={(o) => !o && !busy && setPreview(null)}>
        <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto" data-testid="qb-bulk-confirm">
          {preview && <>
            <DialogHeader>
              <DialogTitle>Confirm: {BULK[preview.action].label}</DialogTitle>
              <DialogDescription>The server re-checked status{preview.action === "delete" ? " and exam-history references" : ", format, answer keys, syllabus mapping and duplicates"}. Only eligible questions will change.</DialogDescription>
            </DialogHeader>
            {BULK[preview.action].warn && <p className="text-sm rounded-lg border border-amber-200 bg-amber-50 text-amber-800 p-3" data-testid="qb-confirm-warning">{BULK[preview.action].warn}</p>}
            {preview.action === "delete" && <p className="text-xs text-slate-600" data-testid="qb-confirm-delete-split">{preview.results.filter((x) => x.delete_mode === "hard").length} will be permanently removed · {preview.results.filter((x) => x.delete_mode === "soft").length} will be soft-deleted (used in exams or published)</p>}
            <div className="flex gap-3 text-sm"><Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700" data-testid="qb-confirm-eligible">{preview.summary.eligible} eligible</Badge>
              <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700" data-testid="qb-confirm-ineligible">{preview.summary.skipped + preview.summary.failed} ineligible</Badge></div>
            <ResultList results={preview.results} onOpen={onOpen} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreview(null)} disabled={!!busy} data-testid="qb-confirm-cancel">Cancel</Button>
              <Button className={BULK[preview.action].cls} disabled={!!busy || !preview.summary.eligible} onClick={confirm} data-testid="qb-confirm-go">
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{BULK[preview.action].label} {preview.summary.eligible} question{preview.summary.eligible === 1 ? "" : "s"}
              </Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
      <Dialog open={!!report} onOpenChange={(o) => !o && setReport(null)}>
        <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto" data-testid="qb-bulk-report">
          {report && <>
            <DialogHeader><DialogTitle>{BULK[report.action].label} — completed</DialogTitle></DialogHeader>
            <p className="text-sm" data-testid="qb-report-summary"><span className="text-emerald-700 font-medium">{report.summary.success} succeeded</span> · <span className="text-amber-700">{report.summary.skipped} skipped</span> · <span className="text-red-700">{report.summary.failed} failed</span></p>
            <ResultList results={report.results} onOpen={onOpen} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setReport(null)} data-testid="qb-report-close">Close</Button>
              {report.summary.skipped + report.summary.failed > 0 && <Button onClick={() => check(report.action, report.results.filter((x) => x.outcome !== "success").map((x) => x.id))} disabled={!!busy} data-testid="qb-report-retry">Re-check & retry unsuccessful</Button>}
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ResultList({ results, onOpen }) {
  return (
    <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg text-sm" data-testid="qb-result-list">
      {results.map((x) => (
        <div key={x.id} className="p-3" data-testid={`qb-result-${x.id}`}>
          <div className="flex items-start justify-between gap-3">
            <button type="button" className="text-left text-slate-800 hover:text-blue-700 line-clamp-2" onClick={() => onOpen(x.id)}>{x.stem || x.id}</button>
            <span className={`shrink-0 text-xs font-medium ${OUT_CLS[x.outcome]}`}>{x.outcome}{x.delete_mode ? ` · ${x.delete_mode === "hard" ? "permanent" : "soft"}` : ""}{x.to === "removed" ? " (removed)" : ""}</span>
          </div>
          {x.reasons.map((r, i) => <p key={i} className="text-xs text-red-600 mt-1">• {r}</p>)}
          {x.warnings.map((w, i) => <p key={i} className="text-xs text-amber-600 mt-1">⚠ {w}</p>)}
        </div>
      ))}
    </div>
  );
}
